import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Option } from '@/components/ui/select'
import { Select } from '@/components/ui/select'
import { useUserList } from '@/features/user'
import {
  useCreateUserMapping,
  useUpdateUserMapping,
  useUserMappingList,
} from '@/features/user-mapping/hooks'
import { useUserMappingStore } from '@/features/user-mapping/store'
import { formatFullName } from '@/utils/common'
import {
  DEFAULT_GET_ALL_LIMIT,
  DEFAULT_PAGE,
  SortDirection,
} from '@/utils/constants'

import { GET_TEAMWORK_PAGINATION_LIMIT, UserMappingSource } from '../constants'

interface UserMappingFormData {
  vista_user_id: string
  teamwork_user_id: string
  teamwork_username: string
  teamwork_user_email: string
}

const UserMappingDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    editingUserMapping,
    dialogOpen: open,
    clearEditingUserMapping,
    setDialogOpen,
  } = useUserMappingStore()

  const { data: { data: vistaUsers = [] } = {} } = useUserList({
    page: DEFAULT_PAGE,
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
  })

  const { data: { data: teamworkUsers = [] } = {} } = useUserMappingList({
    page: DEFAULT_PAGE,
    rowsPerPage: GET_TEAMWORK_PAGINATION_LIMIT,
    sort: { column: 'teamwork_user_id', direction: SortDirection.ASC },
    source: UserMappingSource.TEAMWORK,
  })

  const isEditMode = useMemo(() => !!editingUserMapping, [editingUserMapping])

  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm<UserMappingFormData>({
    mode: 'onChange',
    defaultValues: {
      vista_user_id: '',
      teamwork_user_id: '',
      teamwork_username: '',
      teamwork_user_email: '',
    },
  })

  const vistaUserId = watch('vista_user_id')
  const teamworkUserId = watch('teamwork_user_id')

  const createMutation = useCreateUserMapping()
  const updateMutation = useUpdateUserMapping(
    editingUserMapping?.mapping_id || ''
  )

  const handleApiError = (error: unknown) => {
    const axiosError = error as AxiosError<{ error?: { message?: string } }>
    const errorMessage =
      axiosError.response?.data?.error?.message ||
      t('userMapping.dialog.messages.errorGeneric')

    setError('vista_user_id', {
      type: 'manual',
      message: errorMessage,
    })
  }

  useEffect(() => {
    if (isEditMode && editingUserMapping) {
      setValue('vista_user_id', editingUserMapping.vista_user_id || '')
      setValue('teamwork_user_id', editingUserMapping.teamwork_user_id || '')
      setValue('teamwork_username', editingUserMapping.teamwork_username || '')
      setValue(
        'teamwork_user_email',
        editingUserMapping.teamwork_user_email || ''
      )
    }
  }, [isEditMode, editingUserMapping, setValue])

  useEffect(() => {
    if (!open) {
      reset({
        vista_user_id: '',
        teamwork_user_id: '',
        teamwork_username: '',
        teamwork_user_email: '',
      })
      createMutation.reset()
      updateMutation.reset()
      if (isEditMode) {
        clearEditingUserMapping()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset])

  const onSubmit = (data: UserMappingFormData) => {
    const formData = {
      vista_user_id: data.vista_user_id.trim(),
      teamwork_user_id: data.teamwork_user_id.trim(),
      teamwork_username: data.teamwork_username.trim(),
      teamwork_user_email: data.teamwork_user_email.trim(),
    }

    if (isEditMode && editingUserMapping) {
      updateMutation.mutate(formData, {
        onSuccess: () => {
          triggerToast(
            'success',
            t('userMapping.dialog.messages.updateSuccess')
          )
          clearEditingUserMapping()
          setDialogOpen(false)
        },
        onError: handleApiError,
      })
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          triggerToast('success', t('userMapping.dialog.messages.addSuccess'))
          setDialogOpen(false)
        },
        onError: handleApiError,
      })
    }
  }

  const isSaving = useMemo(
    () => createMutation.isPending || updateMutation.isPending,
    [createMutation.isPending, updateMutation.isPending]
  )

  const handleOpenChange = (newOpen: boolean) => {
    setDialogOpen(newOpen)
    if (!newOpen && isEditMode) {
      clearEditingUserMapping()
    }
  }

  const vistaUserOptions: Option[] = useMemo(() => {
    if (!vistaUsers?.length) return []

    // Precompute mapped vista user IDs for efficient lookup
    const mappedVistaUserIds = new Set(
      teamworkUsers
        .filter(
          (user) =>
            // Exclude the currently edited user when in edit mode
            !(
              isEditMode &&
              user.vista_user_id === editingUserMapping?.vista_user_id
            ) &&
            user.vista_user_id !== null &&
            user.vista_user_id !== undefined
        )
        .map((user) => user.vista_user_id)
    )

    const availableVistaUsers = vistaUsers.filter((user) => {
      // In edit mode, allow currently edited user to stay in the list
      if (isEditMode && user.id === editingUserMapping?.vista_user_id) {
        return true
      }
      return !mappedVistaUserIds.has(user.id)
    })

    return availableVistaUsers.map((user) => ({
      value: user.id,
      label: (
        <div className="flex items-center gap-2">
          <span className="font-medium text-primary-900">
            {formatFullName(user.first_name, user.last_name)}
          </span>
          <span className="font-normal text-tertiary-600">{user.email}</span>
        </div>
      ),
    }))
  }, [vistaUsers, teamworkUsers, isEditMode, editingUserMapping?.vista_user_id])

  const teamworkUserOptions: Option[] = useMemo(() => {
    const editingTeamworkUserId =
      isEditMode && editingUserMapping
        ? editingUserMapping.teamwork_user_id
        : null

    const notMappedTeamworkUsers = teamworkUsers.filter(
      (user) =>
        !user.has_mapping || user.teamwork_user_id === editingTeamworkUserId
    )

    return notMappedTeamworkUsers.map(
      (user) =>
        ({
          value: user.teamwork_user_id,
          label: (
            <>
              <span className="font-medium text-primary-900">
                {user.teamwork_username}
              </span>
              <span className="ml-2 font-normal text-tertiary-600">
                {user.teamwork_user_email}
              </span>
            </>
          ),
        }) as Option
    )
  }, [editingUserMapping, isEditMode, teamworkUsers])

  const isFormValid = useMemo(() => {
    return isValid && !!vistaUserId && !!teamworkUserId
  }, [isValid, vistaUserId, teamworkUserId])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 text-lg font-semibold text-primary-900">
              {t('userMapping.dialog.addTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {t('userMapping.dialog.addDescription')}
            </DialogDescription>
          </DialogHeader>

          <form
            id="user-mapping-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <div className="space-y-1">
              <label htmlFor="vista_user_id" className="text-sm font-medium">
                {t('userMapping.dialog.fields.vistaUser')}
              </label>
              <Select
                value={vistaUserId}
                onChange={(value) =>
                  setValue('vista_user_id', value, { shouldValidate: true })
                }
                options={vistaUserOptions}
                placeholder={t(
                  'userMapping.dialog.placeholders.selectVistaUser'
                )}
                disabled={isSaving}
                fullWidth
                noMatchesText="Already mapped users are hidden from list."
              />
              {errors.vista_user_id && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.vista_user_id.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label htmlFor="teamwork_user_id" className="text-sm font-medium">
                {t('userMapping.dialog.fields.teamworkUser')}
              </label>
              <Select
                value={teamworkUserId}
                onChange={(value) => {
                  setValue('teamwork_user_id', value, { shouldValidate: true })
                  const selectedUser = teamworkUsers.find(
                    (user) => user.teamwork_user_id === value
                  )
                  if (selectedUser) {
                    setValue(
                      'teamwork_username',
                      selectedUser.teamwork_username || '',
                      { shouldValidate: true }
                    )
                    setValue(
                      'teamwork_user_email',
                      selectedUser.teamwork_user_email || '',
                      { shouldValidate: true }
                    )
                  }
                }}
                options={teamworkUserOptions}
                placeholder={t(
                  'userMapping.dialog.placeholders.selectTeamworkUser'
                )}
                disabled={isSaving}
                fullWidth
                noMatchesText="Already mapped users are hidden from list."
              />
              {errors.teamwork_user_id && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.teamwork_user_id.message}
                </p>
              )}
            </div>
          </form>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-md border-solid border-primary bg-white hover:bg-primary-hover"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="user-mapping-form"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid border-transparent !text-white bg-brand-solid hover:bg-brand-solid-hover disabled:bg-gray-light-100 disabled:!text-gray-light-400 disabled:hover:bg-gray-light-200"
              variant="default"
              disabled={isSaving || !isFormValid}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default UserMappingDialog
